import { useCallback } from "react";

import { AUTH_COOKIE_NAME, taco_cookie_ctx } from "@/auth-cookie";
import ApiError from "@/models/ApiError";

const default_headers: Record<string, string> = {
  "content-type": "application/json",
  accept: "application/json",
};

// HeadersInit also covers a Headers instance and an array of name/value pairs,
// and neither of those survives an object spread: the array turns into numeric
// indices and the instance contributes nothing. Going through Headers gives a
// plain record whatever shape the caller passed.
const toHeaderRecord = (headers: HeadersInit | undefined): Record<string, string> => {
  if (headers === undefined) {
    return {};
  }
  return Object.fromEntries(new Headers(headers));
};

export interface ResponseData<T = unknown> {
  json: T;
  status: number;
  is_ok: boolean;
}

// Every api function takes an optional `abort_signal` and forwards it straight
// into `signal`, so the property arrives as an explicit `undefined` whenever the
// caller has no signal. RequestInit's own optional properties reject that under
// exactOptionalPropertyTypes, so `signal` is widened here and dropped again
// before the options reach fetch.
export type RequestOptions = Omit<RequestInit, "signal"> & {
  signal?: AbortSignal | null | undefined;
};

export const request = async <T = unknown,>(
  url: string,
  options: RequestOptions = {},
  include_default_headers = true,
): Promise<ResponseData<T>> => {
  const headers = toHeaderRecord(include_default_headers ? default_headers : options.headers);

  const jwt = taco_cookie_ctx.get(AUTH_COOKIE_NAME);
  if (jwt !== undefined) {
    headers.Authorization = `Bearer ${jwt}`;
  }

  const { signal, ...rest } = options;
  const final_options: RequestInit = { ...rest, headers };
  if (signal) {
    final_options.signal = signal;
  }

  const response = await fetch(url, final_options);
  let response_body: T;
  try {
    response_body = await response.json();
  } catch {
    response_body = {} as T;
  }

  return {
    json: response_body,
    status: response.status,
    is_ok: response.ok,
  };
};

export const useRequest = (abort_signal: AbortSignal) => {
  return useCallback(
    <T = unknown,>(
      url: string,
      options: RequestOptions = {},
      include_default_headers = true,
    ): Promise<ResponseData<T>> => {
      // The signal is the fallback, not the default: spreading `options` over it
      // would let an explicit `signal: undefined` from the caller strip the abort
      // this hook exists to attach.
      const final_options: RequestOptions = {
        ...options,
        signal: options.signal ?? abort_signal,
      };
      return request<T>(url, final_options, include_default_headers).catch((err: Error) => {
        if (err.name === "AbortError") {
          return {
            status: 0,
            is_ok: false,
            json: { message: "Request aborted" } as T,
          };
        } else {
          throw err;
        }
      });
    },
    [abort_signal],
  );
};

export interface HandleResponseReturn<T> {
  data: T | null;
  error: ApiError | null;
}

export function handleResponse<T>(response: ResponseData<unknown>): HandleResponseReturn<T> {
  if (response.is_ok) {
    return { data: response.json as T, error: null };
  }
  return { data: null, error: new ApiError(response) };
}
